// The step-by-step QC inspection (QC inspector role, phone and desktop): the ISO 2859-1 sampling plan, the AQL result
// worked out by Falcon, the checklist built from Settings → QC (per product type) and the Tech Pack revision issued with
// the PO, the five steps, and the checks behind the red boxes. Shared by the server (which enforces and
// re-checks everything on submit) and the screens (which show it live).
import { AQL_CHOICES, STANDARD_LEVELS, codeLetter, lotRanges } from "./quality.js";

// ---- ISO 2859-1, normal inspection, single sampling (Table 2-A) ------------------------------------------------------
/** The sample size of each code letter. */
export const ISO_SAMPLE_SIZE: Record<string, number> = { A: 2, B: 3, C: 5, D: 8, E: 13, F: 20, G: 32, H: 50, J: 80, K: 125, L: 200, M: 315, N: 500, P: 800, Q: 1250, R: 2000 };
const LETTERS = Object.keys(ISO_SAMPLE_SIZE);
/** The AQL columns the table holds. */
export const ISO_AQLS = ["0.65", "1", "1.5", "2.5", "4", "6.5"] as const;
/** Accept numbers (reject = accept + 1). "d" = use the first plan below the arrow, "u" = the first plan above it. */
const TABLE: Record<string, Array<number | "d" | "u">> = {
  A: ["d", "d", "d", "d", "d", 0], B: ["d", "d", "d", "d", 0, "u"], C: ["d", "d", "d", 0, "u", "d"], D: ["d", "d", 0, "u", "d", 1],
  E: ["d", 0, "u", "d", 1, 2], F: [0, "u", "d", 1, 2, 3], G: ["u", "d", 1, 2, 3, 5], H: ["d", 1, 2, 3, 5, 7],
  J: [1, 2, 3, 5, 7, 10], K: [2, 3, 5, 7, 10, 14], L: [3, 5, 7, 10, 14, 21], M: [5, 7, 10, 14, 21, "u"],
  N: [7, 10, 14, 21, "u", "u"], P: [10, 14, 21, "u", "u", "u"], Q: [14, 21, "u", "u", "u", "u"], R: [21, "u", "u", "u", "u", "u"],
};
export const QC_INSPECTION_LEVELS: Array<[string, string]> = [["I", "General level I"], ["II", "General level II"], ["III", "General level III"], ["S-1", "Special S-1"], ["S-2", "Special S-2"], ["S-3", "Special S-3"], ["S-4", "Special S-4"], ["100%", "100% (every piece)"]];
/** The AQL choices of the QC standard (the options of the earlier report form). */
export const AQL_LEVEL_CHOICES = AQL_CHOICES;
void STANDARD_LEVELS;
/** "2.5", "4.0", "0.65", "0 (none allowed)". */
export const aqlLabel = (a: string) => (a === "0" ? "0 (none allowed)" : Number(a) >= 1 && !String(a).includes(".") ? `${a}.0` : a);

export interface ClassPlan { aql: string; letter: string; sampleSize: number; accept: number; reject: number }
/** One class (Critical, Major or Minor): follow the arrows to the plan that applies. AQL 0 = nothing allowed. */
export function isoClassPlan(letter: string, aql: string): ClassPlan {
  if (String(aql) === "0" || !(ISO_AQLS as readonly string[]).includes(String(parseFloat(aql)))) {
    return { aql: String(aql), letter, sampleSize: ISO_SAMPLE_SIZE[letter] || 2, accept: 0, reject: 1 };
  }
  const col = (ISO_AQLS as readonly string[]).indexOf(String(parseFloat(aql)));
  let i = Math.max(0, LETTERS.indexOf(letter));
  for (let guard = 0; guard < 20; guard++) {
    const cell = TABLE[LETTERS[i]][col];
    if (cell === "d") { i = Math.min(LETTERS.length - 1, i + 1); continue; }
    if (cell === "u") { i = Math.max(0, i - 1); continue; }
    return { aql: String(aql), letter: LETTERS[i], sampleSize: ISO_SAMPLE_SIZE[LETTERS[i]], accept: cell, reject: cell + 1 };
  }
  return { aql: String(aql), letter, sampleSize: ISO_SAMPLE_SIZE[letter] || 2, accept: 0, reject: 1 };
}
export interface SamplingPlan {
  lotSize: number; level: string; codeLetter: string; sampleSize: number; fullInspection: boolean;
  critical: ClassPlan; major: ClassPlan; minor: ClassPlan;
}
/**
 * The sample to check for a lot (the finished quantity) at an inspection level, and the defects allowed per class at the
 * organisation's AQL levels. When the classes' plans have different sample sizes the largest is checked (each class
 * keeps its own accept number); a sample as large as the lot means checking every piece.
 */
export function samplingPlan(lotSize: number, level: string, aql: { critical: string; major: string; minor: string }): SamplingPlan {
  const lot = Math.max(0, Math.floor(Number(lotSize) || 0));
  if (level === "100%") {
    // Every piece is checked; nothing is allowed.
    const all = (a: string): ClassPlan => ({ aql: a, letter: "100%", sampleSize: lot, accept: 0, reject: 1 });
    return { lotSize: lot, level, codeLetter: "100%", sampleSize: lot, fullInspection: lot > 0, critical: all(aql.critical), major: all(aql.major), minor: all(aql.minor) };
  }
  const letter = codeLetter(Math.max(2, lot), QC_INSPECTION_LEVELS.some(([k]) => k === level) ? level : "II");
  const c = isoClassPlan(letter, aql.critical), m = isoClassPlan(letter, aql.major), n = isoClassPlan(letter, aql.minor);
  const want = Math.max(c.sampleSize, m.sampleSize, n.sampleSize);
  const sampleSize = lot > 0 ? Math.min(want, lot) : 0;
  return { lotSize: lot, level, codeLetter: letter, sampleSize, fullInspection: lot > 0 && want >= lot, critical: c, major: m, minor: n };
}

// ---- Settings → QC: the QC checklist per product type -----------------------------------------------------------------
export type GroupMode = "required" | "optional" | "hidden";
export const GROUP_MODES: Array<[GroupMode, string]> = [["required", "Required"], ["optional", "Optional"], ["hidden", "Hidden"]];
/** How a trim type is checked: Required, Optional, or left out ("Don't check"). */
export type TrimMode = "required" | "optional" | "skip";
export const TRIM_MODES: Array<[TrimMode, string]> = [["required", "Required"], ["optional", "Optional"], ["skip", "Don't check"]];
/** The sections of "Photos and checks", in this order (Other checks and the video come after them). */
export type SectionKey = "packing" | "labels" | "product" | "trims";
export const SECTION_KEYS: SectionKey[] = ["packing", "labels", "product", "trims"];
/** Older settings named the sections that came from the tech pack; kept for reading old settings. */
export const TECHPACK_GROUPS: Array<["packing" | "labels" | "trims", string]> = [["packing", "Packing"], ["labels", "Labels and tags"], ["trims", "Trims and artwork"]];
/** Trims without a trim type, and artwork placed on the product, are grouped under these names. */
export const NO_TRIM_TYPE = "No type";
export const ARTWORK_TYPE = "Artwork";
/** `tr`: the check's wording in other languages ({ ar: "…", zh: "…" }), shown to inspectors who use that language
 *  (optional; the office always sees the English name). */
export interface ChecklistPhoto { id: string; name: string; required: boolean; tr?: Record<string, string> }
export interface Checklist {
  id: string; name: string; categories: string[]; measuredLabel: string; componentsLabel: string;
  /** The product photos (the section titled by the product type). */
  photos: ChecklistPhoto[];
  packing: ChecklistPhoto[];
  labels: ChecklistPhoto[];
  /** Each section: Required (each item's own Req / Opt), Optional (every item optional) or Hidden (left out). */
  sections: Record<SectionKey, GroupMode>;
  /** Trim types (from the trims kept in Falcon): Required / Optional / Don't check. A type not listed is Optional. */
  trimTypes: Record<string, TrimMode>;
}
/** Settings → QC (the AQL plan itself is the QC standard: Quality → QC → Inspection Standard). */
export interface QcWizardSettings {
  /** Each piece outside measurement tolerance is added to the Major count. */
  measureAsMajor: boolean;
  videoRequired: boolean;
  /** Photos and video may also be chosen from the phone's gallery (off: the camera only). */
  allowGallery: boolean;
  checklists: Checklist[];
}
const item = (id: string, name: string, required = true): ChecklistPhoto => ({ id, name, required });
const SECTIONS_DEFAULT: Record<SectionKey, GroupMode> = { packing: "required", labels: "required", product: "required", trims: "optional" };
export const DEFAULT_CHECKLISTS: Checklist[] = [
  { id: "garment", name: "Garment", categories: [], measuredLabel: "Garments measured", componentsLabel: "Trims and artwork",
    photos: [item("front", "Garment front"), item("back", "Garment back"), item("inside", "Inside and seams")],
    packing: [item("carton", "Carton and carton marks"), item("carton-label", "Carton label"), item("polybag", "Polybag"), item("polybag-sticker", "Polybag sticker", false), item("folding", "Folding", false)],
    labels: [item("swing-tag", "Swing tag"), item("wash-care", "Wash care label"), item("brand-label", "Brand label"), item("size-label", "Size label", false)],
    sections: { ...SECTIONS_DEFAULT }, trimTypes: {} },
  { id: "footwear", name: "Footwear", categories: [], measuredLabel: "Pairs measured", componentsLabel: "Components",
    photos: [item("side", "Pair side view"), item("top", "Top view"), item("sole", "Sole"), item("inside", "Inside and insole"), item("toe", "Toe cap and heel")],
    packing: [item("shoe-box", "Shoe box"), item("carton", "Carton and carton marks"), item("carton-label", "Carton label"), item("tissue", "Tissue and stuffing", false)],
    labels: [item("swing-tag", "Swing tag"), item("size-label", "Size label"), item("box-label", "Box label", false)],
    sections: { ...SECTIONS_DEFAULT }, trimTypes: {} },
  { id: "general", name: "General", categories: [], measuredLabel: "Pieces measured", componentsLabel: "Trims and artwork",
    photos: [item("front", "Front"), item("back", "Back"), item("detail", "Inside or detail")],
    packing: [item("carton", "Carton and carton marks"), item("carton-label", "Carton label"), item("inner-pack", "Inner packing", false)],
    labels: [item("swing-tag", "Swing tag"), item("product-label", "Product label", false)],
    sections: { ...SECTIONS_DEFAULT }, trimTypes: {} },
];
export const DEFAULT_QC_SETTINGS: QcWizardSettings = { measureAsMajor: true, videoRequired: false, allowGallery: false, checklists: DEFAULT_CHECKLISTS };
const str = (v: unknown, max = 200) => String(v ?? "").trim().slice(0, max);
const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
/** A check's own wording in other languages: { ar, zh, … } (codes from shared/i18n), each up to 120 characters. */
export function normalizeTr(v: unknown): Record<string, string> | undefined {
  if (!v || typeof v !== "object") return undefined;
  const out: Record<string, string> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    if (/^[a-z]{2}(-[A-Za-z]{2,4})?$/.test(k) && k !== "en") { const t = str(x, 120); if (t) out[k] = t; }
  }
  return Object.keys(out).length ? out : undefined;
}
const groupMode = (x: unknown, def: GroupMode): GroupMode => (["required", "optional", "hidden"].includes(String(x)) ? x as GroupMode : def);
function items(list: unknown, fallback: ChecklistPhoto[] | null): ChecklistPhoto[] {
  if (!Array.isArray(list)) return (fallback || []).map((x) => ({ ...x }));
  const out: ChecklistPhoto[] = [];
  for (const p of list) {
    const pn = str((p as any)?.name, 80);
    if (!pn) continue;
    let id = str((p as any)?.id, 40) || slug(pn) || `p${out.length + 1}`;
    while (out.some((x) => x.id === id)) id = `${id}-${out.length + 1}`;
    const tr = normalizeTr((p as any)?.tr);
    out.push({ id, name: pn, required: (p as any)?.required !== false, ...(tr ? { tr } : {}) });
  }
  return out.slice(0, 40);
}
/** A checklist as stored; `oldGroups` are the organisation-wide section switches of the earlier Settings → QC. */
export function normalizeChecklist(c: any, i: number, oldGroups?: Partial<Record<"packing" | "labels" | "trims", unknown>>): Checklist | null {
  const name = str(c?.name, 60);
  if (!name) return null;
  const id = str(c?.id, 40) || slug(name) || `type${i + 1}`;
  const def = DEFAULT_CHECKLISTS.find((d) => d.id === id) || DEFAULT_CHECKLISTS.find((d) => d.id === "general")!;
  const sec = (k: SectionKey) => groupMode(c?.sections?.[k] ?? (k === "product" ? undefined : oldGroups?.[k]), SECTIONS_DEFAULT[k]);
  const trimTypes: Record<string, TrimMode> = {};
  for (const [k, v] of Object.entries(c?.trimTypes && typeof c.trimTypes === "object" ? c.trimTypes : {})) {
    const t = str(k, 80);
    if (t && ["required", "optional", "skip"].includes(String(v))) trimTypes[t] = v as TrimMode;
  }
  return {
    id, name, categories: [...new Set((Array.isArray(c?.categories) ? c.categories : []).map((x: unknown) => str(x, 80)).filter(Boolean))] as string[],
    // The trims heading is "Trims and artwork" (Components for footwear); the earlier default wordings move to it.
    measuredLabel: str(c?.measuredLabel, 60) || "Pieces measured", componentsLabel: str(c?.componentsLabel, 80).replace(/ on the garment$/i, "").replace(/^Trims and components$/i, "Trims and artwork") || "Trims and artwork",
    photos: items(c?.photos, []), packing: items(c?.packing, def.packing), labels: items(c?.labels, def.labels),
    sections: { packing: sec("packing"), labels: sec("labels"), product: sec("product"), trims: sec("trims") }, trimTypes,
  };
}
export function normalizeQcSettings(v: any): QcWizardSettings {
  const d = DEFAULT_QC_SETTINGS;
  const lists: Checklist[] = [];
  for (const [i, c] of (Array.isArray(v?.checklists) ? v.checklists : d.checklists).entries()) {
    const n = normalizeChecklist(c, i, v?.groups);
    if (n && !lists.some((x) => x.id === n.id)) lists.push(n);
  }
  return {
    measureAsMajor: v?.measureAsMajor === undefined ? d.measureAsMajor : !!v.measureAsMajor, videoRequired: !!v?.videoRequired, allowGallery: !!v?.allowGallery,
    checklists: lists.length ? lists.slice(0, 30) : DEFAULT_CHECKLISTS.map((c) => normalizeChecklist(c, 0)!),
  };
}
/** The default lists of a product type (Settings → QC → Reset to defaults). */
export function defaultChecklist(c: Pick<Checklist, "id" | "name" | "categories" | "measuredLabel" | "componentsLabel">): Checklist {
  const def = DEFAULT_CHECKLISTS.find((d) => d.id === c.id) || DEFAULT_CHECKLISTS.find((d) => d.id === "general")!;
  return { ...normalizeChecklist({ ...def, id: c.id, name: c.name, categories: c.categories, measuredLabel: c.measuredLabel, componentsLabel: c.componentsLabel }, 0)!, trimTypes: {} };
}
const FOOTWEAR = /\b(foot\s*wear|shoes?|boots?|sandals?|trainers?|sneakers?|clogs?|slippers?|wellingtons?)\b/i;
/** The product type (checklist) of a style: its category listed on a checklist; else footwear categories use the
 *  Footwear checklist, garments the Garment one, anything else General. */
export function checklistFor(s: QcWizardSettings, category: string, itemType: string): Checklist {
  const cat = String(category || "").trim().toLowerCase();
  const listed = cat ? s.checklists.find((c) => c.categories.some((x) => x.toLowerCase() === cat)) : undefined;
  if (listed) return listed;
  const byId = (id: string) => s.checklists.find((c) => c.id === id);
  if (FOOTWEAR.test(cat) && byId("footwear")) return byId("footwear")!;
  if (itemType === "product" && byId("garment")) return byId("garment")!;
  return byId("general") || s.checklists[s.checklists.length - 1];
}

// ---- The words that follow the product type (one list for the wizard, the report, the PDF and Settings → QC) ---------
export type ProductKind = "garment" | "footwear" | "other";
/** The kind of a checklist: Garment, Footwear (or a footwear-sounding name), anything else. */
export function productKindOfChecklist(c: Pick<Checklist, "id" | "name">): ProductKind {
  if (c.id === "footwear" || FOOTWEAR.test(c.name)) return "footwear";
  if (c.id === "garment" || /^garments?$/i.test(c.name.trim())) return "garment";
  return "other";
}
export interface ProductWords {
  /** The product section's title, e.g. "Garment", "Footwear", or the product type's own name ("Product" if none). */
  title: string;
  /** "Garment photos", "Footwear photos"… */
  photos: string;
  measured: string;
  /** The trims section: "Trims and artwork" ("Components" for footwear). */
  components: string;
  /** Short unit after numbers ("pcs" / "pairs") and the word in sentences ("pieces" / "pairs"). */
  unit: string; unitWord: string;
  /** The product in a sentence: "garment", "footwear", "product". */
  noun: string;
}
export function productWords(snap: Pick<QcSnapshot, "productType" | "measuredLabel" | "componentsLabel" | "productKind">): ProductWords {
  // The general product type reads "Product".
  const name = String(snap.productType || "").trim();
  const title = !name || /^general$/i.test(name) ? "Product" : name;
  const kind: ProductKind = snap.productKind || productKindOfChecklist({ id: "", name: title });
  const pairs = kind === "footwear";
  // Older inspections kept "Trims and artwork on the garment"; the section is now "Trims and artwork" (Components for footwear).
  const comp = String(snap.componentsLabel || "").replace(/ on the garment$/i, "");
  return {
    title, photos: `${title} photos`,
    measured: snap.measuredLabel || (pairs ? "Pairs measured" : kind === "garment" ? "Garments measured" : "Pieces measured"),
    components: comp || (pairs ? "Components" : "Trims and artwork"),
    unit: pairs ? "pairs" : "pcs", unitWord: pairs ? "pairs" : "pieces",
    noun: kind === "garment" ? "garment" : pairs ? "footwear" : "product",
  };
}
/** The section titles of "Photos and checks" for a product type: Packing, Labels and tags, Garment / Footwear / Product,
 *  Trims and artwork (Components for footwear), Other checks, Video. */
export function sectionTitle(key: SectionKey | "other" | "video", words: Pick<ProductWords, "title" | "components">): string {
  return key === "packing" ? "Packing" : key === "labels" ? "Labels and tags" : key === "product" ? words.title : key === "trims" ? words.components : key === "other" ? "Other checks" : "Video";
}

// ---- The checklist of one inspection (items) ---------------------------------------------------------------------------
export type ItemGroup = SectionKey;
/** A reference picture from the Tech Pack: an image file, or a label / packaging design drawn from its data. */
export type QcRef = { type: "image"; fileId: string } | { type: "design"; kind: string; design: unknown; data: unknown };
export interface QcItem {
  key: string; group: ItemGroup; name: string; detail: string;
  /** The tech pack has a reference for it: the eye button and "Matches the tech pack?". */
  fromTechpack: boolean; required: boolean; ref: QcRef | null;
  /** Trims: the trim type it was grouped under. */
  trimType?: string;
  /** Item keys an earlier version of the inspection used for the same check (kept when drafts move to a new layout). */
  aliases?: string[];
  /** The check's wording in other languages (from Settings → QC or the trim), for inspectors using that language. */
  tr?: Record<string, string>;
}
/** What the server finds in the Tech Pack revision issued with the PO. */
export interface ItemSource {
  checklist: Checklist;
  designs: Array<{ kind: string; label: string; group: "packing" | "labels"; ref: QcRef | null }>;
  bom: Array<{ key: string; group: "packing" | "labels" | "trims"; name: string; detail: string; ref: QcRef | null; trimType?: string; tr?: Record<string, string> }>;
}
/** Words that tie a Settings item (e.g. "Wash care label") to the tech pack's label or packaging (e.g. design "wash_care"). */
const MATCH_WORDS: Array<[RegExp, RegExp]> = [
  [/carton (and carton )?marks?|^carton$/i, /^carton$|carton (and carton )?marks?|shipping carton|outer carton/i],
  [/carton label/i, /carton[_ ]label|carton sticker/i],
  [/polybag sticker|bag sticker|size sticker/i, /size[_ ]sticker|polybag sticker/i],
  [/^polybag$|poly ?bag(?! sticker)/i, /^polybag$|poly ?bag(?! sticker)/i],
  [/swing ?tag|hang ?tag/i, /swing[_ ]?tag|hang ?tag/i],
  [/wash ?care|care label/i, /wash[_ ]?care|care label/i],
  [/brand label|main label|woven label/i, /brand[_ ]label|main.*label|woven label/i],
  [/size label|size tab/i, /size[_ ]?(label|tab)/i],
  [/ppe|fr .*label|certification label/i, /ppe|fr .*label|certification/i],
  [/shoe ?box/i, /shoe ?box/i],
  [/box label/i, /box label/i],
];
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
function matches(settingsName: string, tp: { kind?: string; label: string }): boolean {
  const a = norm(settingsName), b = norm(tp.label), k = String(tp.kind || "");
  if (a && (a === b || b.startsWith(a) || a.startsWith(b))) return true;
  return MATCH_WORDS.some(([s, t]) => s.test(settingsName) && (t.test(k) || t.test(tp.label)));
}
/** The checks of an inspection: the product type's Packing, Labels and tags and product photos from Settings → QC
 *  (with the tech pack's reference where it has one), anything else the tech pack has for packing and labels, and the
 *  tech pack's trims and artwork whose trim type is checked. Hidden sections are left out. */
export function candidateItems(src: ItemSource, s: QcWizardSettings | null): QcItem[] {
  void s;
  const c = src.checklist;
  const out: QcItem[] = [];
  const req = (sec: SectionKey, own: boolean) => c.sections[sec] === "required" && own;
  for (const sec of ["packing", "labels"] as const) {
    if (c.sections[sec] === "hidden") continue;
    const tps = [...src.designs.filter((d) => d.group === sec).map((d) => ({ key: `design:${d.kind}`, kind: d.kind, label: d.label, detail: "", ref: d.ref })),
      ...src.bom.filter((b) => b.group === sec).map((b) => ({ key: b.key, kind: "", label: b.name, detail: b.detail, ref: b.ref }))];
    const used = new Set<string>();
    for (const x of c[sec]) {
      const hits = tps.filter((t) => !used.has(t.key) && matches(x.name, t));
      hits.forEach((h) => used.add(h.key));
      const withRef = hits.find((h) => h.ref) || hits[0];
      out.push({ key: `${sec === "packing" ? "pk" : "lb"}:${x.id}`, group: sec, name: x.name, detail: withRef?.detail || "", fromTechpack: !!withRef?.ref, required: req(sec, x.required), ref: withRef?.ref || null, aliases: hits.map((h) => h.key), ...(x.tr ? { tr: x.tr } : {}) });
    }
    // The tech pack's own labels / packaging that no Settings item covers: checked too (optional), with the reference.
    for (const t of tps.filter((t) => !used.has(t.key))) {
      if (out.some((o) => o.group === sec && norm(o.name) === norm(t.label))) continue;
      out.push({ key: t.key, group: sec, name: t.label, detail: t.detail, fromTechpack: true, required: false, ref: t.ref });
    }
  }
  if (c.sections.product !== "hidden") for (const p of c.photos) out.push({ key: `photo:${p.id}`, group: "product", name: p.name, detail: "", fromTechpack: false, required: req("product", p.required), ref: null, ...(p.tr ? { tr: p.tr } : {}) });
  if (c.sections.trims !== "hidden") {
    for (const b of src.bom.filter((x) => x.group === "trims")) {
      const type = b.trimType || NO_TRIM_TYPE;
      const mode = c.trimTypes[type] || "optional";
      if (mode === "skip") continue;
      out.push({ key: b.key, group: "trims", name: b.name, detail: b.detail, fromTechpack: true, required: c.sections.trims === "required" && mode === "required", ref: b.ref, trimType: type, ...(b.tr ? { tr: b.tr } : {}) });
    }
  }
  return out;
}

// ---- The inspection snapshot (frozen at start) -----------------------------------------------------------------------
export interface SizeSpec { unit: string; sizes: string[]; rows: Array<{ pom: string; tolerance: string; values: Record<string, string> }> }
/** Kept on reports made before the per-PO QC plan was removed (what that plan left out). */
export interface PlanExclusion { key: string; label: string; by: string; at: string }
export interface QcSnapshot {
  po: { id: string; number: string };
  line: { id: string; code: string; description: string; colour: string; quantity: number; unit: string; sizes: string[]; qtyBySize: Record<string, number>; category: string };
  productType: string; measuredLabel: string; componentsLabel: string;
  /** Garment, footwear or another product type (from the product's checklist); older inspections work it out from the name. */
  productKind?: ProductKind;
  techpack: { id: string; revision: number; label: string; newer: boolean; latestRevision: number } | null;
  items: QcItem[];
  /** Only on reports made with the earlier per-PO QC plan ("Not included in this inspection"); new reports have none. */
  excluded: Array<PlanExclusion & { group: ItemGroup | "" }>;
  sizeSpec: SizeSpec | null;
  /** The QC standard the inspection was started with (kept with it), and the Settings → QC switches. */
  settings: { aqlCritical: string; aqlMajor: string; aqlMinor: string; level: string; measureAsMajor: boolean; videoRequired: boolean; allowGallery?: boolean };
  goldSeal: { number: string; name: string; photos: string[] } | null;
  /** Files the inspection may show (reference pictures, gold seal photos). */
  files: string[];
  /** A draft started in the earlier single-page QC form: everything it held, shown read-only with the inspection. */
  legacy?: LegacyQc;
  /** 2: the five-step inspection ("Photos and checks" in sections). Older inspections have no layout. */
  layout?: number;
  /** Sections of "Photos and checks" left out (Hidden in Settings → QC). */
  hiddenSections?: SectionKey[];
  /** Trims and artwork: how many the tech pack has (shown under the heading). */
  trimCount?: number;
  /** When the inspection was started (gallery photos taken before then are flagged). */
  startedAt?: string;
  /** When an earlier draft moved to the five-step layout. */
  convertedAt?: string;
}
export interface LegacyQc {
  reportNo: string; movedAt: string; movedBy: string;
  rows: Array<[string, string]>;
  checkpoints: Array<{ label: string; result: string; resultCode?: string; notes: string }>;
  defects: Array<{ severity: string; severityCode?: string; qty: number; description: string; location: string; correctiveAction: string }>;
  measurements: Array<{ pom: string; size: string; spec: string; tolerance: string; actual: string; result: string; resultCode?: string }>;
  notes: Array<[string, string]>;
}

/** Steps: 1 Inspection, 2 Photos and checks, 3 Workmanship, 4 Measurements, 5 Result. Reports made before the five
 *  steps also used Packing, Labels and tags and the product step (read when a draft moves to the new layout). */
export type StepKey = "inspection" | "checks" | "workmanship" | "measurements" | "result" | "packing" | "labels" | "product";
export const FIVE_STEPS: Array<{ key: StepKey; title: string }> = [
  { key: "inspection", title: "Inspection" }, { key: "checks", title: "Photos and checks" }, { key: "workmanship", title: "Workmanship" },
  { key: "measurements", title: "Measurements" }, { key: "result", title: "Result" },
];
export function wizardSteps(_snap?: unknown): Array<{ key: StepKey; title: string }> {
  return FIVE_STEPS.map((s) => ({ ...s }));
}
/** The steps an inspection had before the five-step layout (to keep a draft on the matching step). */
export function oldWizardSteps(snap: Pick<QcSnapshot, "items" | "productType">): StepKey[] {
  const out: StepKey[] = ["inspection"];
  if (snap.items.some((i) => i.group === "packing")) out.push("packing");
  if (snap.items.some((i) => i.group === "labels")) out.push("labels");
  out.push("product", "workmanship", "measurements", "result");
  return out;
}

// ---- The inspector's answers -----------------------------------------------------------------------------------------
export type Severity4 = "none" | "minor" | "major" | "critical";
export const SEVERITY4: Array<[Severity4, string]> = [["none", "No defect"], ["minor", "Minor"], ["major", "Major"], ["critical", "Critical"]];
/** Where a photo or video came from: the camera, the phone's gallery, or a file on a computer. */
export type MediaSource = "camera" | "gallery" | "upload";
export interface WizardPhoto { id: string; item: string; note: string; severity: Severity4; source?: MediaSource; takenAt?: string }
export interface WizardPoint { pom: string; size: string; spec: string; tolerance: string; measured: string; piece: string; manual: boolean }
/** A check the inspector added ("Add another check"): its own title, photos, "Is it OK?" and notes. */
export interface AddedCheck { key: string; section: SectionKey | "other"; title: string; match: "" | "yes" | "no"; reason: string; notes: string }
export interface WizardData {
  finishedQty: number | null;
  /** Pieces actually checked; below the standard's sample the defects allowed follow the quantity checked. */
  checkedQty: number | null;
  /** Why fewer pieces than the standard requires were checked (optional). */
  belowReason: string;
  sizes: Record<string, number | null>;
  answers: Record<string, { match: "" | "yes" | "no"; reason: string }>;
  photos: WizardPhoto[];
  added: AddedCheck[];
  critical: number | null; major: number | null; minor: number | null;
  measured: number | null; withinTolerance: "" | "yes" | "no"; points: WizardPoint[];
  video: { id: string; name: string; status: string; source?: MediaSource; takenAt?: string } | null;
  comments: string;
}
/** Item key of the "Other photos" of inspections made before "Other checks" (still read on old reports). */
export const OTHER_PHOTOS = "other";
export const ADDED_KEY = /^added:[a-z0-9]{4,40}$/;
const intOrNull = (v: unknown) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Math.floor(Number(String(v).replace(/,/g, "")));
  return Number.isFinite(n) && n >= 0 ? Math.min(n, 100_000_000) : null;
};
const source = (v: unknown): MediaSource | undefined => (v === "camera" || v === "gallery" || v === "upload" ? v : undefined);
const isoOrEmpty = (v: unknown) => { const t = str(v, 40); return t && !Number.isNaN(Date.parse(t)) ? new Date(t).toISOString() : ""; };
export function normalizeWizard(x: any): WizardData {
  const answers: WizardData["answers"] = {};
  for (const [k, v] of Object.entries(x?.answers && typeof x.answers === "object" ? x.answers : {})) {
    const a = v as any;
    if (k.length > 120) continue;
    answers[k] = { match: a?.match === "yes" || a?.match === "no" ? a.match : "", reason: str(a?.reason, 1000) };
  }
  const sizes: WizardData["sizes"] = {};
  for (const [k, v] of Object.entries(x?.sizes && typeof x.sizes === "object" ? x.sizes : {})) if (k.length <= 30) sizes[k] = intOrNull(v);
  const added: AddedCheck[] = [];
  for (const c of Array.isArray(x?.added) ? x.added.slice(0, 200) : []) {
    const key = str(c?.key, 50);
    if (!ADDED_KEY.test(key) || added.some((a) => a.key === key)) continue;
    const section = ["packing", "labels", "product", "trims", "other"].includes(c?.section) ? c.section : "other";
    added.push({ key, section, title: str(c?.title, 120), match: c?.match === "yes" || c?.match === "no" ? c.match : "", reason: str(c?.reason, 1000), notes: str(c?.notes, 1000) });
  }
  const photo = (p: any): WizardPhoto => {
    const out: WizardPhoto = { id: str(p?.id, 60), item: str(p?.item, 120), note: str(p?.note, 1000), severity: (["none", "minor", "major", "critical"].includes(p?.severity) ? p.severity : "none") as Severity4 };
    const s = source(p?.source), t = isoOrEmpty(p?.takenAt);
    if (s) out.source = s;
    if (t) out.takenAt = t;
    return out;
  };
  let video: WizardData["video"] = null;
  if (x?.video && /^[0-9a-f-]{36}$/i.test(String(x.video.id || ""))) {
    video = { id: String(x.video.id), name: str(x.video.name, 200), status: str(x.video.status, 40) };
    const s = source(x.video.source), t = isoOrEmpty(x.video.takenAt);
    if (s) video.source = s;
    if (t) video.takenAt = t;
  }
  return {
    finishedQty: intOrNull(x?.finishedQty), checkedQty: intOrNull(x?.checkedQty), belowReason: str(x?.belowReason, 1000), sizes, answers,
    photos: (Array.isArray(x?.photos) ? x.photos : []).slice(0, 2000).map(photo).filter((p: WizardPhoto) => /^[0-9a-f-]{36}$/i.test(p.id)),
    added,
    critical: intOrNull(x?.critical), major: intOrNull(x?.major), minor: intOrNull(x?.minor),
    measured: intOrNull(x?.measured), withinTolerance: x?.withinTolerance === "yes" || x?.withinTolerance === "no" ? x.withinTolerance : "",
    points: (Array.isArray(x?.points) ? x.points : []).slice(0, 500).map((p: any) => ({
      pom: str(p?.pom, 200), size: str(p?.size, 30), spec: str(p?.spec, 40), tolerance: str(p?.tolerance, 40), measured: str(p?.measured, 40), piece: str(p?.piece, 10), manual: !!p?.manual,
    })),
    video, comments: str(x?.comments, 5000),
  };
}
export const blankWizard = (): WizardData => normalizeWizard({});
/** "Camera", "Gallery · taken 12/10/2026 09:30", "Uploaded from computer"; `early` when a gallery item was taken before the inspection started. */
export function sourceLabel(m: { source?: MediaSource; takenAt?: string }, startedAt?: string, fmt: (iso: string) => string = (s) => s.slice(0, 16).replace("T", " ")): { text: string; early: boolean } {
  if (!m.source) return { text: "", early: false };
  if (m.source === "camera") return { text: "Camera", early: false };
  if (m.source === "upload") return { text: "Uploaded from computer", early: !!(m.takenAt && startedAt && m.takenAt < startedAt) };
  return { text: m.takenAt ? `Gallery · taken ${fmt(m.takenAt)}` : "Gallery", early: !!(m.takenAt && startedAt && m.takenAt < startedAt) };
}

// ---- Measurements ----------------------------------------------------------------------------------------------------
/** A measurement written as 52, 52.5, 52,5, 20 1/2 or 1/4. */
export function parseMeasure(v: string): number | null {
  const t = String(v ?? "").trim().replace(/(\d),(\d)/g, "$1.$2");
  if (!t) return null;
  const m = /^(-?\d+(?:\.\d+)?)?\s*(?:(\d+)\/(\d+))?\s*[a-z"']*$/i.exec(t);
  if (!m || (!m[1] && !m[2])) return null;
  const whole = m[1] ? Number(m[1]) : 0, frac = m[2] && Number(m[3]) ? Number(m[2]) / Number(m[3]) : 0;
  const n = whole < 0 ? whole - frac : whole + frac;
  return Number.isFinite(n) ? Math.round(n * 10000) / 10000 : null;
}
/** A tolerance written as ±1, +/-1, 1, +1/-0.5 or 0.5 cm: how much above and below the spec is allowed. */
export function parseTolerance(v: string): { plus: number; minus: number } | null {
  const t = String(v ?? "").trim().replace(/(\d),(\d)/g, "$1.$2");
  if (!t) return null;
  const NUM = "(\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:\\.\\d+)?)";
  const SYM = /±|\+\s*\/\s*-|\+\s*-|-\s*\/\s*\+/;
  const first = (x: string) => { const n = new RegExp(NUM).exec(x); return n ? parseMeasure(n[1]) : null; };
  if (SYM.test(t)) {
    const x = first(t.replace(new RegExp(SYM.source, "g"), " "));
    return x === null ? null : { plus: x, minus: x };
  }
  const p = new RegExp(`\\+\\s*${NUM}`).exec(t), m = new RegExp(`-\\s*${NUM}`).exec(t);
  if (p || m) {
    const plus = p ? parseMeasure(p[1]) : 0, minus = m ? parseMeasure(m[1]) : 0;
    return plus === null || minus === null ? null : { plus, minus };
  }
  const x = first(t);
  return x === null ? null : { plus: Math.abs(x), minus: Math.abs(x) };
}
const fmt = (n: number) => String(Math.round(n * 100) / 100);
export interface PointCheck { specText: string; toleranceText: string; spec: number | null; measured: number | null; diff: number | null; outside: boolean | null; beyond: number | null; text: string }
/** A point out of tolerance: what it should be (from the size spec, or typed by hand), the difference and how far outside. */
export function checkPoint(p: WizardPoint, spec: SizeSpec | null): PointCheck {
  const row = !p.manual && spec ? spec.rows.find((r) => r.pom === p.pom) : undefined;
  const specText = p.manual ? p.spec : row?.values?.[p.size] ?? "";
  const toleranceText = p.manual ? p.tolerance : row?.tolerance ?? "";
  const s = parseMeasure(specText), m = parseMeasure(p.measured), tol = parseTolerance(toleranceText);
  if (s === null || m === null) return { specText, toleranceText, spec: s, measured: m, diff: null, outside: null, beyond: null, text: "" };
  const diff = Math.round((m - s) * 10000) / 10000;
  const limit = diff >= 0 ? tol?.plus : tol?.minus;
  const outside = tol ? Math.abs(diff) > (limit ?? 0) + 1e-9 : Math.abs(diff) > 1e-9;
  const beyond = outside ? Math.round((Math.abs(diff) - (tol ? limit ?? 0 : 0)) * 10000) / 10000 : 0;
  const unit = spec?.unit ? ` ${spec.unit}` : "";
  const text = `Difference ${diff > 0 ? "+" : diff < 0 ? "−" : ""}${fmt(Math.abs(diff))}${unit} · ${outside ? `${fmt(beyond)}${unit} outside tolerance` : "within tolerance"}`;
  return { specText, toleranceText, spec: s, measured: m, diff, outside, beyond, text };
}
/** Pieces outside tolerance: points with the same piece number are one piece; a point without one counts on its own. */
export function piecesOutside(w: WizardData, spec: SizeSpec | null): number {
  if (w.withinTolerance !== "no") return 0;
  const pieces = new Set<string>();
  let loose = 0;
  for (const p of w.points) {
    if (checkPoint(p, spec).outside !== true) continue;
    if (p.piece.trim()) pieces.add(`${p.size}|${p.piece.trim()}`); else loose++;
  }
  return pieces.size + loose;
}

// ---- The AQL result --------------------------------------------------------------------------------------------------
export interface AqlRow { cls: "Critical" | "Major" | "Minor"; found: number; allowed: number; ok: boolean }
export interface AqlResult {
  plan: SamplingPlan; rows: AqlRow[]; fromMeasurements: number; result: "Pass" | "Fail" | "Incomplete";
  /** The sample the standard requires for the finished quantity, the pieces checked, and whether that is below it. */
  required: number; checked: number | null; below: boolean; sampleLine: string;
}
/**
 * The defects allowed when fewer pieces were checked than the standard requires: the plan of the table row with the
 * largest sample size that is not more than the quantity checked (a class whose plan needs more pieces than that,
 * or fewer than 2 pieces checked, allows nothing).
 */
export function planForChecked(checked: number, level: string, aql: { critical: string; major: string; minor: string }): SamplingPlan {
  const n = Math.max(0, Math.floor(Number(checked) || 0));
  const letter = [...LETTERS].reverse().find((l) => ISO_SAMPLE_SIZE[l] <= n) || "";
  const one = (a: string): ClassPlan => {
    if (!letter || level === "100%") return { aql: a, letter: letter || "-", sampleSize: n, accept: 0, reject: 1 };
    const p = isoClassPlan(letter, a);
    return p.sampleSize > n ? { ...p, sampleSize: n, accept: 0, reject: 1 } : p;
  };
  return { lotSize: n, level, codeLetter: letter || "-", sampleSize: n, fullInspection: false, critical: one(aql.critical), major: one(aql.major), minor: one(aql.minor) };
}
const pcs = (n: number) => Number(n || 0).toLocaleString("en-GB");
/** "Sample below standard: checked 50 pcs, standard requires 80 pcs" or "Sample meets the standard" ("" before it is known). */
export function sampleLine(checked: number | null, required: number): string {
  if (checked === null || !required) return "";
  return checked < required ? `Sample below standard: checked ${pcs(checked)} pcs, standard requires ${pcs(required)} pcs` : "Sample meets the standard";
}
/** Worked out by Falcon (never chosen): Fail if any class has more defects than allowed for the sample. */
export function aqlResult(w: WizardData, snap: Pick<QcSnapshot, "settings" | "sizeSpec">): AqlResult {
  const s = snap.settings;
  const aql = { critical: s.aqlCritical, major: s.aqlMajor, minor: s.aqlMinor };
  const standard = samplingPlan(w.finishedQty || 0, s.level, aql);
  const checked = w.checkedQty;
  const below = checked !== null && standard.sampleSize > 0 && checked < standard.sampleSize;
  const plan = below ? planForChecked(checked!, s.level, aql) : standard;
  const fromMeasurements = s.measureAsMajor ? piecesOutside(w, snap.sizeSpec) : 0;
  const rows: AqlRow[] = [
    { cls: "Critical", found: w.critical ?? 0, allowed: plan.critical.accept, ok: true },
    { cls: "Major", found: (w.major ?? 0) + fromMeasurements, allowed: plan.major.accept, ok: true },
    { cls: "Minor", found: w.minor ?? 0, allowed: plan.minor.accept, ok: true },
  ].map((r) => ({ ...r, ok: r.found <= r.allowed })) as AqlRow[];
  const complete = !!w.finishedQty && w.critical !== null && w.major !== null && w.minor !== null;
  return { plan, rows, fromMeasurements, result: !complete ? "Incomplete" : rows.every((r) => r.ok) ? "Pass" : "Fail",
    required: standard.sampleSize, checked, below, sampleLine: sampleLine(checked, standard.sampleSize) };
}

/** The reference table of a standard: finished quantity range → sample required → defects allowed (read-only). */
export interface StandardRow { from: number; to: number | null; sample: number; allOfLot: boolean; critical: number; major: number; minor: number }
export function standardTable(level: string, aql: { critical: string; major: string; minor: string }): StandardRow[] {
  if (level === "100%") return [{ from: 1, to: null, sample: 0, allOfLot: true, critical: 0, major: 0, minor: 0 }];
  return lotRanges(level).map((r) => {
    const p = samplingPlan(Number.isFinite(r.max) ? r.max : r.min, level, aql);
    const want = Math.max(p.critical.sampleSize, p.major.sampleSize, p.minor.sampleSize);
    return { from: r.min, to: Number.isFinite(r.max) ? r.max : null, sample: want, allOfLot: want >= r.min, critical: p.critical.accept, major: p.major.accept, minor: p.minor.accept };
  });
}

// ---- The red boxes ---------------------------------------------------------------------------------------------------
export const MSG = {
  photo: "Add at least one photo", answer: "Answer yes or no", reason: "Say what is wrong", required: "This is required",
  checkedOver: "Can't be more than the finished quantity",
  point: "Complete the point, size and measured size",
  addedPhoto: "Add a photo or remove this check", title: "Enter a title",
} as const;
export interface Problem { key: string; message: string; section?: CheckSection }
/** The sections of step 2 "Photos and checks": the four from Settings, Other checks and the video. */
export type CheckSection = SectionKey | "other" | "video";
export const CHECK_SECTIONS: CheckSection[] = ["packing", "labels", "product", "trims", "other", "video"];
/** The sections shown on step 2 (Hidden ones in Settings → QC are left out; Other checks and the video always show). */
export function visibleSections(snap: Pick<QcSnapshot, "hiddenSections">): CheckSection[] {
  const hidden = new Set(snap.hiddenSections || []);
  return CHECK_SECTIONS.filter((k) => !hidden.has(k as SectionKey));
}
/** One check of step 2: from the checklist (an item) or added by the inspector. */
export type Check = { kind: "item"; key: string; section: CheckSection; item: QcItem } | { kind: "added"; key: string; section: CheckSection; added: AddedCheck };
export function checksOf(snap: Pick<QcSnapshot, "items">, w: Pick<WizardData, "added">, section: CheckSection): Check[] {
  if (section === "video") return [];
  const own: Check[] = section === "other" ? [] : snap.items.filter((i) => i.group === section).map((item) => ({ kind: "item", key: item.key, section, item }));
  return [...own, ...w.added.filter((a) => a.section === section).map((added) => ({ kind: "added" as const, key: added.key, section, added }))];
}
/** What is missing on one check (empty when it is complete). */
export function checkProblems(c: Check, w: WizardData, photoCount: (item: string) => number): string[] {
  const msgs: string[] = [];
  if (c.kind === "item") {
    const it = c.item, a = w.answers[it.key] || { match: "", reason: "" };
    if (it.required && photoCount(it.key) < 1) msgs.push(MSG.photo);
    if (it.fromTechpack && it.required && !a.match) msgs.push(MSG.answer);
    if (a.match === "no" && !a.reason.trim()) msgs.push(MSG.reason);
  } else {
    const a = c.added;
    if (!a.title.trim()) msgs.push(MSG.title);
    if (photoCount(a.key) < 1) msgs.push(MSG.addedPhoto);
    if (!a.match) msgs.push(MSG.answer);
    if (a.match === "no" && !a.reason.trim()) msgs.push(MSG.reason);
  }
  return msgs;
}
/** A check counts as done when nothing is missing and it has a photo or an answer. */
export function checkDone(c: Check, w: WizardData, photoCount: (item: string) => number): boolean {
  if (checkProblems(c, w, photoCount).length) return false;
  return c.kind === "added" || photoCount(c.key) > 0 || !!(w.answers[c.key]?.match);
}
/** One section's count ("2 of 3") and whether it is complete (green). A section with nothing to check is complete. */
export function sectionStatus(section: CheckSection, w: WizardData, snap: QcSnapshot, photoCount: (item: string) => number): { done: number; total: number; complete: boolean; problems: Problem[] } {
  if (section === "video") {
    const need = !!snap.settings.videoRequired;
    return { done: w.video ? 1 : 0, total: 1, complete: !need || !!w.video, problems: need && !w.video ? [{ key: "video", message: MSG.required, section }] : [] };
  }
  const list = checksOf(snap, w, section);
  const problems = list.flatMap((c) => { const m = checkProblems(c, w, photoCount); return m.length ? [{ key: `item:${c.key}`, message: m.join(" · "), section }] : []; });
  return { done: list.filter((c) => checkDone(c, w, photoCount)).length, total: list.length, complete: !problems.length, problems };
}
/** What is missing on one step. `photoCount` counts the photos of an item (including ones still waiting to upload). */
export function stepProblems(step: StepKey, w: WizardData, snap: QcSnapshot, photoCount: (item: string) => number = (k) => w.photos.filter((p) => p.item === k).length): Problem[] {
  const out: Problem[] = [];
  if (step === "inspection") {
    if (!w.finishedQty || w.finishedQty <= 0) out.push({ key: "finishedQty", message: MSG.required });
    if (!w.checkedQty || w.checkedQty <= 0) out.push({ key: "checkedQty", message: MSG.required });
    else if (w.finishedQty && w.checkedQty > w.finishedQty) out.push({ key: "checkedQty", message: MSG.checkedOver });
  } else if (step === "checks") {
    for (const sec of visibleSections(snap)) out.push(...sectionStatus(sec, w, snap, photoCount).problems);
  } else if (step === "workmanship") {
    for (const k of ["critical", "major", "minor"] as const) if (w[k] === null) out.push({ key: k, message: MSG.required });
  } else if (step === "measurements") {
    if (!w.measured || w.measured <= 0) out.push({ key: "measured", message: MSG.required });
    if (!w.withinTolerance) out.push({ key: "withinTolerance", message: MSG.answer });
    if (w.withinTolerance === "no") {
      if (!w.points.length) out.push({ key: "point:0", message: MSG.point });
      w.points.forEach((p, i) => {
        const done = p.pom.trim() && p.size.trim() && parseMeasure(p.measured) !== null && (!p.manual || parseMeasure(p.spec) !== null);
        if (!done) out.push({ key: `point:${i}`, message: MSG.point });
      });
    }
  }
  return out;
}
export function allProblems(w: WizardData, snap: QcSnapshot, photoCount?: (item: string) => number): Array<Problem & { step: StepKey }> {
  return wizardSteps(snap).flatMap((s) => stepProblems(s.key, w, snap, photoCount).map((p) => ({ ...p, step: s.key })));
}
/** Amber notes (never block): sizes checked not adding up to the sample, more pieces than ordered, photo tags above the counts. */
export function sizesTotal(w: WizardData): number {
  return Object.values(w.sizes).reduce((t: number, v) => t + (v || 0), 0);
}
export function photoTagCounts(w: WizardData): Record<"critical" | "major" | "minor", number> {
  const out = { critical: 0, major: 0, minor: 0 };
  for (const p of w.photos) if (p.severity !== "none") out[p.severity]++;
  return out;
}
/** Checks answered No (items: "does not match the tech pack"; added checks: "not OK"), with the reason. */
export function mismatches(w: WizardData, snap: Pick<QcSnapshot, "items">): Array<{ item: QcItem; reason: string }> {
  const items = snap.items.filter((i) => w.answers[i.key]?.match === "no").map((item) => ({ item, reason: w.answers[item.key].reason }));
  const added = w.added.filter((a) => a.match === "no").map((a) => ({ item: { key: a.key, group: (a.section === "other" ? "product" : a.section) as ItemGroup, name: a.title || "Added check", detail: "", fromTechpack: false, required: true, ref: null }, reason: a.reason }));
  return [...items, ...added];
}

// ---- The photos of a report, under the headings they were taken under (the Time & Action Plan review) --------------------
export interface PhotoGroupPhoto { id: string; note: string; severity: Severity4 }
export interface PhotoGroupCheck { name: string; added: boolean; match: "" | "yes" | "no"; fromTechpack: boolean; reason: string; notes: string; photos: PhotoGroupPhoto[] }
export interface PhotoGroup { title: string; checks: PhotoGroupCheck[] }
/** The report's photos grouped as in the report: Packing, Labels and tags, the product, Trims and artwork, Other checks,
 *  as on the report. Checks without photos are left out. */
export function photoGroups(snap: Pick<QcSnapshot, "items" | "productType" | "measuredLabel" | "componentsLabel" | "productKind" | "layout">, w: Pick<WizardData, "added" | "photos" | "answers">): PhotoGroup[] {
  const pw = productWords(snap);
  const pics = (key: string) => w.photos.filter((p) => p.item === key).map((p) => ({ id: p.id, note: p.note, severity: p.severity }));
  const out: PhotoGroup[] = [];
  const titles: Record<string, string> = { packing: "Packing", labels: "Labels and tags", product: snap.layout === 2 ? sectionTitle("product", pw) : pw.photos, trims: pw.components, other: snap.layout === 2 ? "Other checks" : "Other photos" };
  for (const sec of ["packing", "labels", "product", "trims", "other"] as const) {
    const checks: PhotoGroupCheck[] = [];
    for (const c of checksOf(snap, w, sec)) {
      const photos = pics(c.key);
      if (!photos.length) continue;
      if (c.kind === "item") { const a = w.answers[c.key]; checks.push({ name: c.item.name, added: false, match: a?.match || "", fromTechpack: c.item.fromTechpack, reason: a?.reason || "", notes: "", photos }); }
      else checks.push({ name: c.added.title || "Added check", added: true, match: c.added.match, fromTechpack: false, reason: c.added.reason, notes: c.added.notes, photos });
    }
    if (sec === "other" && pics(OTHER_PHOTOS).length) checks.push({ name: "Other photos", added: false, match: "", fromTechpack: false, reason: "", notes: "", photos: pics(OTHER_PHOTOS) });
    if (checks.length) out.push({ title: titles[sec], checks });
  }
  return out;
}
